import { Button, useDisclosure } from '@chakra-ui/react'
import { faExternalLink } from '@fortawesome/pro-regular-svg-icons'
import { useRouter } from 'next/router'
import { useState } from 'react'

import logger from '@/utils/logger'

import { DIALOGUES_CLUB_PRESIDENT_ROLE_SLUG } from '@/queries/constants'
import VolunteerRoleQ from '@/queries/VolunteerRoleQ'
import { useQuery } from '@/client/context/query'
import { PROGRAM_SLUG_TO_PROGRAM_DETAILS } from '@/client/services/programs/config'
import {
  PROGRAM_WORLD_TO_PROGRAM_SLUG,
  SAT_BOOTCAMP_PROGRAM_SLUG,
} from '@/client/services/programs/constants'
import { getCapitalizedAndOrPluralizedProgramType } from '@/client/services/programs/helpers'
import { SAT_BOOTCAMP_ONBOARDING_TRAINING_SLUG } from '@/client/services/training'
import {
  getDashboardProgramsUrl,
  getRequestParentConsentUrl,
  getSATBootcampUrl,
  getTutorAuditUrl,
  getTutorOfferingPageUrl,
  getTutorTrainingUrl,
  getUrl,
  getVolunteerRoleUrl,
} from '@/client/services/url'
import { routerMatchesUrl } from '@/client/services/url/helpers'
import { isProgramWorld } from '@/client/services/worlds'

import BackgroundCheckModal from '@/components/BackgroundCheck/BackgroundCheckModal'
import CreateCollegeInfoModal from '@/components/College/CreateCollegeInfoModal'
import CAWConsentModal from '@/components/CollegeAdmissionsWorkshops/CAWConsentModal'
import VerifyAgeButton from '@/components/Profile/AgeDetail/VerifyAgeButton'
import useEditPhoneNumberModal from '@/components/Profile/useEditPhoneNumberModal'
import LearningLabsConsentModal from '@/components/Programs/LearningLabs/LearningLabsConsentModal'
import ShwLink from '@/components/ShwLink'
import Icon from '@/components/UI/Icon/Icon'
import type { WorldDetails } from '@/components/Worlds/types'

export function AgeAssuranceCTA(): JSX.Element {
  return <VerifyAgeButton />
}

export function BackgroundCheckCTA({
  worldSlug,
}: {
  worldSlug?: string | null
}): JSX.Element | null {
  const { onClose, onOpen, isOpen } = useDisclosure()
  if (worldSlug == null) {
    logger.error('BackgroundCheckCTA: worldSlug is null/undefined')
    return null
  }

  return (
    <>
      <BackgroundCheckModal onClose={onClose} isOpen={isOpen} worldSlug={worldSlug} />
      <Button onClick={onOpen}>Proceed</Button>
    </>
  )
}

export function BackgroundCheckInProgressCTA({
  worldSlug,
}: {
  worldSlug?: string | null
}): JSX.Element | null {
  const { onClose, onOpen, isOpen } = useDisclosure()
  if (worldSlug == null) {
    logger.error('BackgroundCheckInProgressCTA: worldSlug is null/undefined')
    return null
  }

  return (
    <>
      <BackgroundCheckModal onClose={onClose} isOpen={isOpen} worldSlug={worldSlug} />
      <Button onClick={onOpen}>Review</Button>
    </>
  )
}

export function GetCertifiedCTA({
  worldSlug,
}: {
  worldSlug?: WorldDetails['slug'] | null
}): JSX.Element | null {
  if (worldSlug == null) {
    logger.error('GetCertifiedCTA: worldSlug is null/undefined')
    return null
  }

  return (
    <ShwLink
      href={
        isProgramWorld(worldSlug)
          ? getTutorOfferingPageUrl({
              type: 'program',
              slug: PROGRAM_WORLD_TO_PROGRAM_SLUG[worldSlug],
              subpage: 'certify',
            })
          : getTutorOfferingPageUrl({ type: 'subject', slug: worldSlug, subpage: 'certify' })
      }
    >
      <Button>Get Certified</Button>
    </ShwLink>
  )
}

export function RequestTutorParentContactCTA(): JSX.Element {
  return (
    <ShwLink href={getSATBootcampUrl('tutor', { path: 'request-contact' })} isExternal>
      <Button rightIcon={<Icon size="md" faIcon={faExternalLink} />}>Enter Parent Contact</Button>
    </ShwLink>
  )
}

export function ReRequestTutorParentConsentCTA(): JSX.Element {
  return (
    <ShwLink href={getSATBootcampUrl('tutor', { path: 'request-consent' })} isExternal>
      <Button variant="outline" rightIcon={<Icon size="md" faIcon={faExternalLink} />}>
        Re-request Consent
      </Button>
    </ShwLink>
  )
}

export function RequestParentConsentCTA(): JSX.Element {
  const router = useRouter()
  const currentPath = router.asPath
  const urlParams = new URLSearchParams({ returnUrl: currentPath })

  return (
    <ShwLink href={getRequestParentConsentUrl(urlParams)} isExternal>
      <Button rightIcon={<Icon size="md" faIcon={faExternalLink} />}>Request Consent</Button>
    </ShwLink>
  )
}

export function ReRequestParentConsentCTA(): JSX.Element {
  const router = useRouter()
  const currentPath = router.asPath
  const urlParams = new URLSearchParams({ returnUrl: currentPath })

  return (
    <ShwLink href={getRequestParentConsentUrl(urlParams)} isExternal>
      <Button variant="outline" rightIcon={<Icon size="md" faIcon={faExternalLink} />}>
        Re-request Consent
      </Button>
    </ShwLink>
  )
}

export function AssentCTA(): JSX.Element {
  const router = useRouter()
  const currentPath = router.asPath
  const returnUrl = encodeURIComponent(currentPath)

  return (
    <ShwLink href={`/assent?returnUrl=${returnUrl}`} isExternal>
      <Button rightIcon={<Icon size="md" faIcon={faExternalLink} />}>Give My Permission</Button>
    </ShwLink>
  )
}

export function BecomeTutorElsewhereCTA(): JSX.Element {
  return (
    <ShwLink href={getUrl('tutor')}>
      <Button>Explore</Button>
    </ShwLink>
  )
}

export function AddPhoneNumberCTA(): JSX.Element {
  const [EditPhoneNumberModal, showEditPhoneNumberModal] = useEditPhoneNumberModal({
    type: 'add',
  })

  return (
    <>
      {EditPhoneNumberModal}
      <Button onClick={() => showEditPhoneNumberModal({})}>Add</Button>
    </>
  )
}

export function CompleteSATBootcampOnboardingTrainingCTA(): JSX.Element {
  return (
    <ShwLink href={getTutorTrainingUrl({ slug: SAT_BOOTCAMP_ONBOARDING_TRAINING_SLUG })} isExternal>
      <Button>Complete Training</Button>
    </ShwLink>
  )
}

export function HostOfferingCTA({
  worldSlug,
}: {
  worldSlug?: WorldDetails['slug'] | null
}): JSX.Element | null {
  const router = useRouter()

  if (worldSlug == null || !isProgramWorld(worldSlug)) {
    logger.error('HostOfferingCTA: worldSlug is null/undefined or not a program world', {
      worldSlug,
    })
    return null
  }

  const programSlug = PROGRAM_WORLD_TO_PROGRAM_SLUG[worldSlug]
  const { programType } = PROGRAM_SLUG_TO_PROGRAM_DETAILS[programSlug]
  const href = getTutorOfferingPageUrl({ type: 'program', slug: programSlug })

  if (routerMatchesUrl(router, href)) {
    return null
  }

  return (
    <ShwLink href={href}>
      <Button>
        Host {getCapitalizedAndOrPluralizedProgramType({ programType, capitalize: true })}
      </Button>
    </ShwLink>
  )
}

export function HostSATBootcampOnboardingSessionCTA(): JSX.Element {
  return (
    <ShwLink
      href={getTutorOfferingPageUrl({
        type: 'program',
        slug: SAT_BOOTCAMP_PROGRAM_SLUG,
      })}
      isExternal
    >
      <Button>Host Session</Button>
    </ShwLink>
  )
}

export function PeerReviewSessionsCTA(): JSX.Element {
  return (
    <ShwLink href={getTutorAuditUrl()} isExternal>
      <Button>Peer Review</Button>
    </ShwLink>
  )
}

export function CoachApplyCTA(): JSX.Element {
  return <Button isDisabled>Coming Soon</Button>
}

export function CollegeVerifyCTA(): JSX.Element {
  const [showModal, setShowModal] = useState(false)

  return (
    <>
      <CreateCollegeInfoModal open={showModal} onClose={() => setShowModal(false)} />
      <Button onClick={() => setShowModal(true)}>Verify College</Button>
    </>
  )
}

export function DialoguesSignUpCTA(): JSX.Element {
  return (
    <ShwLink href={getDashboardProgramsUrl({ programSlug: 'dialogues' })} isExternal>
      <Button rightIcon={<Icon size="md" faIcon={faExternalLink} />}>Sign Up</Button>
    </ShwLink>
  )
}

export function DialoguesClubPresidentApplyCTA(): JSX.Element {
  const query = useQuery()

  const role = query.one(VolunteerRoleQ.where({ slug: DIALOGUES_CLUB_PRESIDENT_ROLE_SLUG }))
  const roleId = role(VolunteerRoleQ.id) ?? 0

  return (
    <ShwLink href={getVolunteerRoleUrl(roleId)} isExternal>
      <Button rightIcon={<Icon size="md" faIcon={faExternalLink} />}>Apply</Button>
    </ShwLink>
  )
}

export function CAWTutorConsentCTA(): JSX.Element {
  const [showModal, setShowModal] = useState(false)

  return (
    <>
      <CAWConsentModal open={showModal} onClose={() => setShowModal(false)} type="tutor" />
      <Button onClick={() => setShowModal(true)}>Complete Consent</Button>
    </>
  )
}

export function LearningLabsTutorConsentCTA({
  worldSlug,
}: {
  worldSlug?: string | null
}): JSX.Element | null {
  const [showModal, setShowModal] = useState(false)

  if (worldSlug == null) {
    logger.error('LearningLabsTutorConsentCTA: worldSlug is null/undefined')
    return null
  }

  return (
    <>
      <LearningLabsConsentModal
        worldSlug={worldSlug}
        open={showModal}
        onClose={() => setShowModal(false)}
        type="tutor"
      />
      <Button onClick={() => setShowModal(true)}>Complete Consent</Button>
    </>
  )
}
